import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { adminApi } from "@/lib/auth";
import { errorText } from "@/lib/errors";
import { Empty, Loading, Mail, useAdminList } from "./shared";

const today = new Date().toISOString().slice(0, 10);

const CancelButton = ({ b, onDone }) => {
  const cancel = async () => {
    try {
      await adminApi.delete(`/admin/bookings/${b.id}`);
      toast.success("Booking cancelled. That time is open again.");
      onDone();
    } catch (err) {
      toast.error(errorText(err));
    }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button type="button" className="inline-flex items-center gap-1 text-sm text-moss hover:text-red-800" data-testid={`booking-cancel-${b.id}`}>
          <Trash2 size={14} /> Cancel
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent className="site bg-paper">
        <AlertDialogHeader>
          <AlertDialogTitle className="dp text-2xl font-normal">Cancel this booking?</AlertDialogTitle>
          <AlertDialogDescription>This removes {b.name}'s session and opens the time for others. Let them know separately.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="booking-cancel-dismiss">Keep it</AlertDialogCancel>
          <AlertDialogAction onClick={cancel} className="bg-pine" data-testid="booking-cancel-confirm">Cancel booking</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export const BookingsTab = () => {
  const { items, reload } = useAdminList("/admin/bookings");
  if (!items) return <Loading />;
  if (!items.length) return <Empty testId="bookings-empty">No sessions booked yet.</Empty>;
  return (
    <ul className="grid gap-4" data-testid="admin-bookings-list">
      {items.map((b) => (
        <li key={b.id} className={`bg-paper rounded-2xl p-6 grid sm:grid-cols-[220px_1fr_auto] gap-4 ${b.date < today ? "opacity-60" : ""}`} data-testid={`admin-booking-${b.id}`}>
          <div>
            <p className="dp text-2xl">{format(parseISO(b.date), "EEE, MMM d")}</p>
            <p className="text-moss tn">{b.time} CT · {b.format}</p>
          </div>
          <div className="min-w-0">
            <p className="font-medium">{b.name}</p>
            <p className="text-sm"><Mail to={b.email} />{b.phone && <> · <a href={`tel:${b.phone}`} className="underline underline-offset-4 decoration-brass">{b.phone}</a></>}</p>
            {b.note && <p className="mt-2 text-sm text-moss whitespace-pre-line">{b.note}</p>}
          </div>
          <CancelButton b={b} onDone={reload} />
        </li>
      ))}
    </ul>
  );
};
